/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { ArcElement, Chart } from "chart.js";
import Typography from "@mui/material/Typography";

import { Colors, colors } from "@dotnet/telematics-design-system";

import {
  COMPANY_CARD_STATUS,
  useFormattedNumber
} from "../../../../../../shared";
import { createEmptyDoughnut } from "../../../../config";
import { useStyles as dashboardStyles } from "../../../../styles";
import { CONNECTIVITY_TABS } from "../../../../types";
import { diameter, options } from "../../config";
import { StatusSummaryType, StatusWidgetDto } from "../../types";
import { DeviceStatus } from "../../types/enums";

Chart.register(ArcElement);

interface DoughnutChartProps {
  statusData: StatusWidgetDto | null;
  setHovered: Dispatch<SetStateAction<boolean>>;
  setHoveredElement: Dispatch<any>;
  setShowSingleStatusInfo: Dispatch<SetStateAction<StatusSummaryType | null>>;
  activeTab?: CONNECTIVITY_TABS;
}

const DoughnutChart = ({
  statusData,
  setHovered,
  setHoveredElement,
  setShowSingleStatusInfo,
  activeTab
}: DoughnutChartProps) => {
  const { t } = useTranslation();

  const [offlineStat, setOfflineStat] = useState<number>(0);
  const [inactiveStat, setInactiveStat] = useState<number>(0);
  const [onlineStat, setOnlineStat] = useState<number>(0);
  const [deactivatedStat, setDeactivatedStat] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { formattedNumber } = useFormattedNumber(totalCount);

  const { canvasHolder, doughnutChartCenterInfo } = dashboardStyles({
    diameter
  });

  useEffect(() => {
    if (!statusData) return;

    const { totalCount, summary } = statusData;

    setTotalCount(totalCount);

    summary.forEach((item: StatusSummaryType) => {
      // if the status is expired, treat it as deactivated
      const updatedName =
        item.name === COMPANY_CARD_STATUS.Expired
          ? DeviceStatus.Deactivated
          : item.name;

      switch (updatedName) {
        case DeviceStatus.Online:
          setOnlineStat(item.count);
          break;
        case DeviceStatus.Offline:
          setOfflineStat(item.count);
          break;
        case DeviceStatus.Inactive:
          setInactiveStat(item.count);
          break;
        case DeviceStatus.Deactivated:
          setDeactivatedStat(item.count);
          break;
        default:
          break;
      }
    });
  }, [
    statusData,
    setOfflineStat,
    setInactiveStat,
    setOnlineStat,
    setDeactivatedStat,
    setTotalCount
  ]);

  const oneDevice = statusData?.totalCount === 1;
  const devicesWidget = activeTab === CONNECTIVITY_TABS.Devices || !activeTab;

  // Data array for the chart
  const statusArray = [onlineStat, offlineStat, inactiveStat, deactivatedStat];

  const maxNumberInStatusArray = statusArray
    .filter(i => i !== 0)
    .reduce((a, b) => Math.max(a, b), 0);

  // empty space width is 1% of the max number in the status array
  const emptySpace = maxNumberInStatusArray * 0.01;

  // check if data array has more then two elements to be able to manipulate spacing between elements
  const hasMoreThenTwoElements = statusArray.filter(i => i !== 0).length > 2;

  const checkIfOnlyOneOptionHasValue = () => {
    return statusArray.filter((item: number) => item > 0).length === 1;
  };

  //  check if data array element value is not 0
  const hasValueByStatusArrayIndex = (index: number) =>
    statusArray[index] !== 0;

  const checkIfSingleStatusHasValue = () => {
    const statusArrayWithoutZero = statusArray.filter(i => i !== 0);
    return statusArrayWithoutZero.length === 1;
  };

  const insertEmptySpaceValue = (
    index: number,
    value: number | Colors | string
  ) => hasMoreThenTwoElements && hasValueByStatusArrayIndex(index) && value;

  const insertEmptySpace = (index: number) =>
    insertEmptySpaceValue(index, emptySpace);

  const insertEmptySpaceColor = (index: number) => {
    const emptySpaceColor = colors.white;
    return insertEmptySpaceValue(index, emptySpaceColor);
  };

  const insertEmptySpaceBorder = (index: number) => {
    const emptySpaceBorder = "transparent";
    return insertEmptySpaceValue(index, emptySpaceBorder);
  };

  const dataArrayBackgroundColors = [
    colors.green700,
    insertEmptySpaceColor(0),
    colors.red700,
    insertEmptySpaceColor(1),
    colors.white,
    insertEmptySpaceColor(2),
    colors.gray200,
    insertEmptySpaceColor(3)
  ];

  const data: any = {
    datasets: [
      {
        data: [
          statusArray[0],
          // Since spacing between data elements can be manipulated only with white border color,
          // we need to add empty space with white color to separate chart elements.
          insertEmptySpace(0),
          statusArray[1],
          insertEmptySpace(1),
          statusArray[2],
          insertEmptySpace(2),
          statusArray[3],
          insertEmptySpace(3)
        ],
        labels: [
          DeviceStatus.Online,
          undefined,
          DeviceStatus.Offline,
          undefined,
          DeviceStatus.Inactive,
          undefined,
          DeviceStatus.Deactivated
        ],
        backgroundColor: dataArrayBackgroundColors,
        hoverBackgroundColor: dataArrayBackgroundColors,
        borderRadius: checkIfOnlyOneOptionHasValue() ? 0 : 2,
        borderWidth: 1,
        borderColor: [
          colors.green700, // Online status
          insertEmptySpaceBorder(0),
          statusData?.totalCount === 0 ? colors.gray200 : colors.red700, // Offline status
          insertEmptySpaceBorder(1),
          colors.gray400, // Inactive status
          insertEmptySpaceBorder(2),
          colors.gray400, // Deactivated status
          insertEmptySpaceBorder(3)
        ],
        // This property works only with two or less elements in the chart.
        spacing: checkIfSingleStatusHasValue()
          ? 0
          : !hasMoreThenTwoElements && !oneDevice && 2
      }
    ]
  };

  const handleOnMouseOut = () => {
    setHovered(false);
    setHoveredElement(null);
  };

  const handleOnPointerEnter = (e: any) => {
    setHovered(true);
  };

  const onlineStatus = statusData?.summary.find(
    i => i.name === DeviceStatus.Online
  );
  const offlineStatus = statusData?.summary.find(
    i => i.name === DeviceStatus.Offline
  );
  const inactiveStatus = statusData?.summary.find(
    i => i.name === DeviceStatus.Inactive
  );
  const deactivatedStatus = statusData?.summary.find(
    i =>
      i.name === DeviceStatus.Deactivated ||
      i.name === COMPANY_CARD_STATUS.Expired
  );

  const handleElementClick = (element: { index: number }) => {
    switch (element?.index) {
      case 0:
        setShowSingleStatusInfo(onlineStatus as StatusSummaryType);
        break;
      case 2:
        setShowSingleStatusInfo(offlineStatus as StatusSummaryType);
        break;
      case 4:
        setShowSingleStatusInfo(inactiveStatus as StatusSummaryType);
        break;
      case 6:
        setShowSingleStatusInfo(deactivatedStatus as StatusSummaryType);
        break;
      default:
        break;
    }
  };

  const emptyDoughnutPlugin = useMemo(
    () => createEmptyDoughnut(statusData),
    [statusData]
  );

  return (
    <div
      id="canvas-holder"
      style={canvasHolder as {}}
      data-testid="canvas-holder"
    >
      <Doughnut
        id="doughnut-chart"
        onPointerLeave={totalCount > 0 ? handleOnMouseOut : undefined}
        onPointerEnter={totalCount > 0 ? handleOnPointerEnter : undefined}
        data={data}
        options={options(data, setHoveredElement, handleElementClick)}
        width={+diameter}
        height={+diameter}
        plugins={[emptyDoughnutPlugin]}
        style={{ cursor: "pointer" }}
      />

      {/* centered total count of devices */}
      <div css={css(doughnutChartCenterInfo)}>
        {statusData && (
          <>
            <div>
              <Typography variant="body1Medium">{formattedNumber}</Typography>
            </div>
            <div>
              <Typography variant="body2Medium">
                {devicesWidget
                  ? t("Dashboard##devices")
                  : t("Dashboard##cards")}
              </Typography>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DoughnutChart;
